import React, { useState, useRef, useEffect } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { GripVertical, Trash2, Paperclip, ExternalLink } from 'lucide-react';

const OrderItemRow = ({ item, index, hasEditPermission, onSelect, onRemove, getSongMeta }) => {
  const itemSongs = item.songs || [];
  const itemAttachments = item.attachments || [];
  const itemLinks = item.links || [];
  const hasInlineContent = itemSongs.length > 0 || itemAttachments.length > 0 || itemLinks.length > 0;
  const hasFiles = itemAttachments.length > 0 || itemLinks.length > 0;

  // Two-tap delete guard: first click arms the button (turns red), auto-disarms after 2 s,
  // second click within that window confirms the deletion.
  const [armed, setArmed] = useState(false);
  const armTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(armTimerRef.current), []);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (!armed) {
      setArmed(true);
      clearTimeout(armTimerRef.current);
      armTimerRef.current = setTimeout(() => setArmed(false), 2000);
    } else {
      clearTimeout(armTimerRef.current);
      setArmed(false);
      onRemove(item.id);
    }
  };

  return (
    <Draggable draggableId={item.id} index={index} isDragDisabled={!hasEditPermission}>
      {(itemDrag, itemSnap) => (
        <div
          ref={itemDrag.innerRef}
          {...itemDrag.draggableProps}
          className={`flex flex-col rounded-lg border px-4 py-3 mb-1.5 last:mb-0 transition-all cursor-pointer ${
            itemSnap.isDragging
              ? 'bg-green-50 border-green-200 shadow-md'
              : 'bg-white dark:!bg-[#1a2540] border-slate-200 dark:border-slate-600/50 hover:border-green-200'
          }`}
          data-testid={`order-item-${item.id}`}
          onClick={() => onSelect(item.id)}
        >
          <div className="flex items-center gap-3">
            <div
              {...itemDrag.dragHandleProps}
              className="cursor-grab active:cursor-grabbing"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4 text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{item.name || item.title}</p>
            </div>
            {hasEditPermission && (
              <button
                type="button"
                onClick={handleDeleteClick}
                title={armed ? 'Click again to confirm delete' : 'Remove item'}
                className={`p-1 rounded transition-all duration-150 flex-shrink-0 ${
                  armed
                    ? 'text-white bg-rose-500 hover:bg-rose-600 scale-110'
                    : 'text-slate-400 hover:text-rose-600'
                }`}
                data-testid={`remove-item-${item.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {hasInlineContent && (
            <div className="mt-2 space-y-0.5" data-testid={`item-songs-${item.id}`}>
              {itemSongs.map((s) => {
                const meta = getSongMeta(s.song_id);
                const title = s.title || meta.title || 'Unknown';
                const key = meta.key;
                const tempo = meta.bpm;
                return (
                  <div key={s.song_id} className="flex items-start gap-2 text-xs text-slate-500 pl-1">
                    <span className="text-green-300 flex-shrink-0 mt-0.5">&#9835;</span>
                    <span className="break-words">
                      {title}
                      {key && <span className="text-slate-400 ml-1">&middot; {key}</span>}
                    </span>
                  </div>
                );
              })}
              {hasFiles && itemSongs.length > 0 && <div className="h-1.5" />}
              {itemAttachments.map((att) => (
                <div key={att.attachment_id || att.filename} className="flex items-center gap-2 text-xs text-slate-500 pl-1">
                  <Paperclip className="w-3 h-3 text-slate-300 flex-shrink-0" />
                  <span className="truncate">{att.label || att.filename}</span>
                </div>
              ))}
              {itemLinks.map((link) => (
                <div key={link.id} className="flex items-center gap-2 text-xs text-slate-500 pl-1">
                  <ExternalLink className="w-3 h-3 text-green-300 flex-shrink-0" />
                  <span className="truncate">{link.label || link.title || link.url}</span>
                </div>
              ))}
            </div>
          )}
          {item.notes && <p className="text-xs text-slate-500 mt-1.5 whitespace-pre-wrap break-words">{item.notes}</p>}
        </div>
      )}
    </Draggable>
  );
};

export default OrderItemRow;
